/**
 * §14.6 degradation ladder, §14.1 B1/B6 enforcement, DECISIONS.md AMD-2 device tiers.
 *
 * Three jobs live here and nothing else does.
 *
 *  1. **Device tier.** AMD-2 turns spine §9's single set of maxima into three:
 *     `desktop` is the spine reference (500/1500/3000), `standard` and `handheld`
 *     are the phone-shaped ones. The tier is a ceiling on entity counts and on
 *     internal buffer size, chosen once by {@link PerfGovernor.probe} and
 *     overridable in Settings.
 *  2. **The ladder.** Ten cumulative rungs of visual concession. Rungs 0–5 are
 *     spine-locked (§14.6): decorative particles, then trails, then parallax.
 *     Rungs 6–9 are AMD-2's appendix: persistence, halation, bloom levels,
 *     buffer scale + DPR. The §14.6 exempt set — telegraphs, hazard fills, enemy
 *     outlines, elite pulse, spawn portals, status cues, damage numbers, UI,
 *     target indicator — appears on no rung and never will.
 *  3. **The loop that walks it.** §14.6 pins the triggers: up one rung when the
 *     GPU frame EMA exceeds 7.0 ms for 30 consecutive frames with a 120-frame
 *     cooldown; down when the EMA sits under 4.5 ms for 600 frames. That rule is
 *     the steady-state governor and it is deliberately slow. A density spike is
 *     not steady state, so a second, faster path runs beside it: a
 *     severity-weighted overrun integrator that walks a rung roughly every 30
 *     frames while the frame is badly over budget, which recovers a 3-rung cliff
 *     inside about a second.
 *
 * Four rules shape the whole file.
 *
 *  - **The tier is a ceiling, never a floor.** Every rung is a cap over what the
 *    tier already allows. Rung 0 is exactly the tier, so a player who picks
 *    `desktop` can be walked down to protect the frame rate and can never be
 *    silently walked up past their own choice.
 *  - **p95, never the mean.** A mean hides a hitch by averaging it against the
 *    twenty smooth frames either side. The p95 is the frame the player feels.
 *  - **Asymmetric time constants.** Degrading is easy and restoring is hard. A
 *    governor that visibly pumps is worse than a low fixed setting, so a degrade
 *    that lands soon after a restore doubles the restore dwell, up to 80 seconds.
 *  - **Gameplay is off the table.** No rung touches spawn logic, hit resolution,
 *    the sim rate or the director's threat budget (spine §9 last line). The tier
 *    changes how many entities may be *alive*; the director pre-check (§8.4)
 *    already denies spawns against that cap, so the game degrades in density,
 *    never in rules.
 *
 * {@link PerfGovernor.sample} allocates nothing. Rings, the percentile scratch
 * and the ladder are all module scope; the only object churn happens when a rung
 * actually changes, which is rare by construction.
 */

import { cfg } from './config';

export type DeviceTier = 'desktop' | 'standard' | 'handheld';

/**
 * What the governor is currently willing to allow, on top of the device tier.
 *
 * Consumers **combine** rather than obey: `min(own, cap)` for the numbers,
 * `own && cap` for the switches. That is what keeps a rung from ever raising
 * quality above the tier the player asked for.
 */
export interface PerfCaps {
  /** §14.1 B2 hard cap on live enemies. AMD-2 tier table. */
  maxEnemies: number;
  /** §14.1 B3 hard cap on live projectiles (player + enemy). */
  maxProjectiles: number;
  /** §14.1 B4 hard cap on live particles. Rung 5 floors this at 1,500. */
  maxParticles: number;
  /** Internal HDR buffer scale (AMD-3 fixed internal resolution × this). */
  bufferScale: number;
  /** Hard cap on `renderer.setPixelRatio`. */
  pixelRatioCap: number;
  /** AMD-3 phosphor persistence pass. */
  persistence: boolean;
  /** AMD-3 halation pass. */
  halation: boolean;
  /** Bloom pyramid levels (§11.1). */
  bloomLevels: number;
  /** Multiplier on decorative particle *emission*. Pool size never changes. */
  particleScale: number;
  /** Multiplier on trail segment count; segments = round(TRAIL_BASE_SEGMENTS × this). */
  trailScale: number;
  /** Background parallax layers (§10). */
  parallaxLayers: number;
  /**
   * Extension beyond the eleven contract fields: §14.6 rung 2 asks for explosion
   * spark counts ×0.5 separately from decorative emission, and there is no other
   * field that can carry it without conflating the two.
   */
  sparkScale: number;
  /** Extension: §14.6 rung 3 asks for trail width ×0.75 alongside the segment cut. */
  trailWidthScale: number;
}

export interface PerfEvent {
  /** `performance.now` at the change. */
  t: number;
  /** Frames sampled at the change, so a headless trace has a clock too. */
  frame: number;
  rung: number;
  label: string;
  /** +1 degraded, -1 restored. */
  dir: 1 | -1;
}

/** Base trail length in segments (§14.6 rung 3 counts down from this). */
export const TRAIL_BASE_SEGMENTS = 12;

/** Bloom pyramid levels at rung 0 (§11.1, AMD-2 rung 8 cuts to 4). */
export const BLOOM_BASE_LEVELS = 6;

/** AMD-2 tier table. `desktop` is the spine §9 reference and the bench gate. */
export const TIER_CAPS: Readonly<Record<DeviceTier, Readonly<PerfCaps>>> = {
  desktop: {
    maxEnemies: 500,
    maxProjectiles: 1500,
    maxParticles: 3000,
    bufferScale: 1,
    pixelRatioCap: 2,
    persistence: true,
    halation: true,
    bloomLevels: BLOOM_BASE_LEVELS,
    particleScale: 1,
    trailScale: 1,
    parallaxLayers: 3,
    sparkScale: 1,
    trailWidthScale: 1,
  },
  standard: {
    maxEnemies: 260,
    maxProjectiles: 800,
    maxParticles: 1600,
    // 810p against a 1080p reference buffer.
    bufferScale: 0.75,
    pixelRatioCap: 1.5,
    persistence: true,
    halation: true,
    bloomLevels: BLOOM_BASE_LEVELS,
    particleScale: 1,
    trailScale: 1,
    parallaxLayers: 3,
    sparkScale: 1,
    trailWidthScale: 1,
  },
  handheld: {
    maxEnemies: 140,
    maxProjectiles: 450,
    maxParticles: 900,
    // 540p against a 1080p reference buffer.
    bufferScale: 0.5,
    pixelRatioCap: 1,
    persistence: true,
    halation: true,
    bloomLevels: BLOOM_BASE_LEVELS,
    particleScale: 1,
    trailScale: 1,
    parallaxLayers: 3,
    sparkScale: 1,
    trailWidthScale: 1,
  },
};

/** Most expensive first. The probe walks this down and stops at the first fit. */
export const TIER_ORDER: readonly DeviceTier[] = ['desktop', 'standard', 'handheld'];

interface LadderStep {
  label: string;
  apply(c: PerfCaps): void;
}

/**
 * The ladder, cumulative, cheapest visual cost first.
 *
 * Rungs 0–5 are spine §9 order restated by §14.6 and are not open to argument:
 * decorative particles thin out, then trails shorten, then the background
 * parallax goes. Rungs 6–9 are AMD-2's appendix, all presentation, none of them
 * in the §14.6 exempt set. The exempt set is absent from this array by
 * construction — that is the whole enforcement mechanism, and
 * `tests/perf.test.ts` asserts the order.
 */
const LADDER: readonly LadderStep[] = [
  // ── §14.6 rungs 1–5, spine-locked ──
  {
    label: 'decorative particles ×0.5',
    apply: (c) => {
      c.particleScale = 0.5;
    },
  },
  {
    label: 'decorative particles ×0.25, explosion sparks ×0.5',
    apply: (c) => {
      c.particleScale = 0.25;
      c.sparkScale = 0.5;
    },
  },
  {
    label: 'trails 12 → 6 segments, width ×0.75',
    apply: (c) => {
      c.trailScale = 6 / TRAIL_BASE_SEGMENTS;
      c.trailWidthScale = 0.75;
    },
  },
  {
    label: 'trails → 4 segments, parallax 3 → 1',
    apply: (c) => {
      c.trailScale = 4 / TRAIL_BASE_SEGMENTS;
      c.parallaxLayers = 1;
    },
  },
  {
    label: 'parallax off, particle cap 1500',
    apply: (c) => {
      c.parallaxLayers = 0;
      if (c.maxParticles > 1500) c.maxParticles = 1500;
    },
  },
  // ── AMD-2 rungs 6–9 ──
  {
    label: 'phosphor persistence off',
    apply: (c) => {
      c.persistence = false;
    },
  },
  {
    label: 'halation off',
    apply: (c) => {
      c.halation = false;
    },
  },
  {
    label: 'bloom 6 → 4 levels',
    apply: (c) => {
      c.bloomLevels = 4;
    },
  },
  {
    label: 'buffer ×0.8, pixel ratio 1',
    apply: (c) => {
      c.bufferScale = c.bufferScale * 0.8;
      c.pixelRatioCap = 1;
    },
  },
];

/** Rolling window in frames. Two seconds at 60 Hz — long enough for an honest p95. */
const WINDOW = 120;
/** Frames before the governor is allowed to touch anything. */
const WARMUP = 24;
/** Frames in the GPU EMA. §14.6's "30 consecutive frames" is the same horizon. */
export const EMA_FRAMES = 30;
const EMA_ALPHA = 2 / (EMA_FRAMES + 1);
/**
 * Integrated overrun, in seconds, that buys one rung on the fast path.
 * Severity-weighted, so a frame at twice budget counts double and a genuine
 * cliff walks a rung in ~0.25 s instead of 0.5 s.
 */
export const DEGRADE_SECONDS = 0.5;
/** Overrun bleeds off at this multiple of real time while under budget. */
const BLEED = 2;
/**
 * Settle time for the fast path, in milliseconds.
 *
 * §14.6's 120-frame cooldown governs the EMA-counter path and is left alone. It
 * is 2 s per rung, which is right for steady drift and far too slow for a surge
 * that arrives in one tick: three rungs would take six seconds and the
 * requirement is one.
 *
 * 500 ms is the reference implementation's number and its reasoning holds. At
 * 250 ms an overloaded frame walked every rung in four seconds, because the
 * accumulator refilled faster than the rung it had just pulled could show up in
 * a GPU timer query that is already several frames stale — the governor was
 * answering its own question before the answer existed. Milliseconds rather than
 * frames because a machine in a cliff *is* running at 30 fps, and a frame-count
 * cooldown silently doubles exactly when it must not.
 */
export const EMERGENCY_SETTLE_MS = 500;
/**
 * Frames the fast path waits regardless of the clock. Staleness in the GPU timer
 * query is measured in frames, so a 240 Hz display still gets a dozen of them.
 */
export const EMERGENCY_MIN_FRAMES = 12;
/**
 * Frames after a restore within which a degrade proves the restore was wrong.
 * 8 s at 60 Hz. This is the anti-pump term.
 */
export const OSCILLATION_FRAMES = 480;
/** Hard ceiling on the doubling restore dwell. 80 s at 60 Hz. */
export const RESTORE_DWELL_MAX_FRAMES = 4800;
/** Headroom. The compositor, the browser and the OS all want part of the frame. */
const BUDGET_FRACTION = 0.9;
/** Restore only with this much margin under budget. The gap is the hysteresis. */
const RESTORE_FRACTION = 0.7;
/** Wall delta above this multiple of the target counts as a dropped frame. */
const DROP_FACTOR = 1.35;
/** Above this the "frame" was a tab switch or a stall, not a frame. Discard it. */
const MAX_FRAME_MS = 250;
/** Frames between windowed (sorting) evaluations. */
const EVAL_EVERY = 15;
/**
 * Consecutive honest frames needed to re-arm after a capture batch.
 *
 * `tools/capture.mjs` steps the game by hand inside a single animation frame. A
 * screenshot has to photograph the tuned look rather than whatever rung the
 * governor happened to be holding, so a batch drops the ladder and stands the
 * governor down until a full second of presented frames has gone by.
 */
const CAPTURE_RELEASE = 60;
const LOG_MAX = 256;

/** Fraction of the frame budget a *quiet* probe frame may spend and still earn a tier. */
const QUIET_FRACTION = 0.5;
/** How far over its allowance a tier may sit and still be kept, with opening rungs. */
const TIER_SLACK = 1.35;
/** Timed frames per candidate tier in the boot probe. */
const PROBE_FRAMES = 6;
/** Hard cap on *timed* probe work, in ms. Warmup is boot's bill, not the probe's. */
export const PROBE_TIMED_BUDGET_MS = 60;
/** Hard cap on everything the probe adds, in ms, compile excluded. */
const PROBE_WALL_BUDGET_MS = 300;
/** Opening rung by how much of its allowance the chosen tier already spends. */
const RUNG_STEPS: readonly { under: number; rung: number }[] = [
  { under: 0.6, rung: 0 },
  { under: 0.85, rung: 1 },
  { under: 1.0, rung: 2 },
  { under: 1.5, rung: 4 },
  { under: 2.5, rung: 6 },
  { under: Infinity, rung: 8 },
];

const STORE_PREFIX = 'lumen.perf.v1';

export interface ProbeVerdict {
  tier: DeviceTier;
  /** Ladder rung the governor opens on. 0 is exactly the tier. */
  rung: number;
  /** Measured quiet-frame cost of the chosen tier, ms. 0 when unmeasured. */
  quietMs: number;
  /** What a quiet frame was allowed to cost. */
  allowanceMs: number;
  /** One line for the settings readout and the bench artifact. */
  reason: string;
  /** True when this came off a fresh measurement rather than out of the store. */
  fresh: boolean;
}

export interface PerfSnapshot {
  tier: DeviceTier;
  rung: number;
  rungs: number;
  label: string;
  autoBalance: boolean;
  generation: number;
  targetMs: number;
  budgetMs: number;
  gpuEmaMs: number;
  frameMsP50: number;
  frameMsP95: number;
  costMsP50: number;
  costMsP95: number;
  samples: number;
  gpuTimed: boolean;
  overrunS: number;
  restoreDwellFrames: number;
  caps: PerfCaps;
  verdict: ProbeVerdict | null;
  timeline: PerfEvent[];
}

function now(): number {
  return typeof performance === 'undefined' ? 0 : performance.now();
}

/** Frame budget for a refresh rate: `targetForHz(120)` → 8.33. */
export function targetForHz(hz: number): number {
  return 1000 / (hz > 1 ? hz : 60);
}

export class PerfGovernor {
  /** Live caps. Read this from a render system; never write to it. */
  readonly caps: PerfCaps = { ...TIER_CAPS.desktop };

  /** Rung changes since boot, newest last. Read by the bench and the dev overlay. */
  readonly log: PerfEvent[] = [];

  /** Frame budget in ms. 16.6 at 60 Hz; a 120 Hz display can ask for 8.3. */
  targetMs = targetForHz(60);

  private tierId: DeviceTier = 'desktop';
  private rungId = 0;
  private gen = 1;
  private auto = true;
  private verdictHeld: ProbeVerdict | null = null;
  private gpuSig = '';

  // ── measurement (nothing past this point allocates) ──
  private readonly frameRing = new Float64Array(WINDOW);
  private readonly costRing = new Float64Array(WINDOW);
  private readonly scratch = new Float64Array(WINDOW);
  private ringHead = 0;
  private ringLen = 0;
  private samples = 0;
  private frames = 0;
  private evalCount = 0;
  private gpuTimed = false;
  private ema = 0;

  private lastSampleMs = -1;
  private lastTimeline = Number.NaN;
  private batched = false;
  private clean = 0;
  private overrun = 0;
  private upFrames = 0;
  private downFrames = 0;
  private changedFrame = -1e9;
  private changedMs = -1e9;
  private restoredFrame = -1e9;
  private restoreDwellFrames = 600;

  constructor() {
    this.restoreDwellFrames = cfg.get('perf.down_frames');
    this.auto = cfg.getBool('perf.auto_balance');
    this.applyRung();
  }

  get tier(): DeviceTier {
    return this.tierId;
  }

  get rung(): number {
    return this.rungId;
  }

  /** Bumped on any tier or rung change. Consumers diff against it before rebuilding. */
  get generation(): number {
    return this.gen;
  }

  get autoBalance(): boolean {
    return this.auto;
  }

  get rungCount(): number {
    return LADDER.length;
  }

  /** What the last rung pulled, for the settings readout. */
  get label(): string {
    return this.rungId === 0 ? 'full quality' : LADDER[this.rungId - 1].label;
  }

  /** The cost we steer to: target minus the headroom everyone else needs. */
  get budgetMs(): number {
    return this.targetMs * BUDGET_FRACTION;
  }

  /** The boot probe's verdict, or null when the probe never ran. */
  get verdict(): ProbeVerdict | null {
    return this.verdictHeld;
  }

  /** Frames the governor has accepted since boot. The headless clock. */
  get frameCount(): number {
    return this.frames;
  }

  /**
   * The unmasked `WEBGL_debug_renderer_info` string, for the store signature.
   * Set it before {@link probe}; a verdict carried over from a different GPU is
   * worse than no verdict, because it is confidently wrong instead of absent.
   */
  setGpuSignature(s: string): void {
    this.gpuSig = s;
  }

  /** 16.6 for 60 Hz, 8.3 for 120. Refresh rate is the caller's business. */
  setTargetMs(ms: number): void {
    const next = ms > 1 ? ms : 1;
    if (next === this.targetMs) return;
    this.targetMs = next;
    this.overrun = 0;
    this.upFrames = 0;
    this.downFrames = 0;
  }

  /**
   * Pick a device tier. `remember` records it as an explicit choice that outranks
   * anything the probe measures on the next load.
   *
   * The rung resets to 0: the player's tier is the new ceiling and the governor
   * re-earns every concession from scratch rather than stacking one ladder on
   * top of another.
   */
  setTier(t: DeviceTier, remember: boolean): void {
    const changed = t !== this.tierId;
    this.tierId = t;
    this.rungId = 0;
    this.applyRung();
    this.gen++;
    this.resetWindow();
    this.writeConfigCaps();
    if (remember) this.rememberChoice(t);
    if (changed && this.log.length > 0) this.pushLog(0, `tier · ${t}`, -1);
  }

  /**
   * The `auto` switch (config `perf.auto_balance`).
   *
   * Turning it off hands every rung back at once: the player asked for their
   * tier, and a half-degraded frozen ladder is not their tier.
   */
  setAutoBalance(on: boolean): void {
    if (this.auto === on) return;
    this.auto = on;
    this.overrun = 0;
    this.upFrames = 0;
    this.downFrames = 0;
    if (!on && this.rungId !== 0) this.setRung(0, -1);
  }

  /**
   * Force a rung. Debug and capture use only — the governor owns this in play.
   */
  setRung(next: number, dir?: 1 | -1): void {
    const clamped = next < 0 ? 0 : next > LADDER.length ? LADDER.length : next | 0;
    if (clamped === this.rungId) return;
    if (dir === undefined) dir = clamped > this.rungId ? 1 : -1;

    // Degrading again right after a restore means the restore was wrong. Make the
    // next one wait twice as long, and keep doubling until it stops.
    if (dir > 0 && this.frames - this.restoredFrame < OSCILLATION_FRAMES) {
      const doubled = this.restoreDwellFrames * 2;
      this.restoreDwellFrames = doubled > RESTORE_DWELL_MAX_FRAMES ? RESTORE_DWELL_MAX_FRAMES : doubled;
    }

    this.rungId = clamped;
    this.changedFrame = this.frames;
    this.changedMs = this.lastSampleMs >= 0 ? this.lastSampleMs : now();
    if (dir < 0) this.restoredFrame = this.frames;
    this.overrun = 0;
    this.upFrames = 0;
    this.downFrames = 0;
    this.applyRung();
    this.gen++;
    this.writeConfigCaps();
    this.pushLog(clamped, this.label, dir);
  }

  /**
   * Where the ladder opens, decided by the boot probe.
   *
   * The rung is a starting point, not a floor: the governor restores from here
   * the moment a window of quiet frames says the probe was pessimistic. That
   * asymmetry is what makes guessing low the safe direction.
   */
  armFrom(rung: number): void {
    this.batched = false;
    this.clean = 0;
    this.lastSampleMs = -1;
    this.lastTimeline = Number.NaN;
    this.overrun = 0;
    if (!this.auto) return;
    // Only ever the opening move: a rung already held, or a window already
    // measured, means the loop has taken over and knows more than the probe.
    if (this.rungId !== 0 || this.samples > 0) return;
    const clamped = rung > LADDER.length ? LADDER.length : Math.floor(rung);
    if (clamped <= 0) return;
    this.rungId = clamped;
    this.applyRung();
    this.gen++;
    this.writeConfigCaps();
    this.pushLog(clamped, `boot probe · ${this.label}`, 1);
  }

  /**
   * One presented frame. Allocates nothing.
   *
   * `gpuMs` is the last clean `EXT_disjoint_timer_query_webgl2` result and may be
   * a few frames stale — fine, the governor integrates before it acts. `cpuMs` is
   * the submission cost. `nowMs` is the frame's start timestamp.
   */
  sample(gpuMs: number, cpuMs: number, gpuValid: boolean, nowMs: number): void {
    // A capture batch runs many renders inside one animation frame, so the
    // document timeline does not advance across them. Those are not frames and
    // measuring them would let the governor degrade a screenshot.
    const advanced = this.timelineAdvanced();
    const prev = this.lastSampleMs;
    this.lastSampleMs = advanced ? nowMs : -1;
    if (!advanced) {
      this.batched = true;
      this.clean = 0;
      this.forceFull();
      return;
    }
    if (this.batched && ++this.clean >= CAPTURE_RELEASE) this.batched = false;
    if (prev < 0) return;

    const frameMs = nowMs - prev;
    if (!(frameMs > 0) || frameMs > MAX_FRAME_MS) return;

    this.gpuTimed = gpuValid;
    // GPU and CPU overlap, so the frame costs whichever finishes last. Without a
    // timer query the GPU is invisible and only dropped frames give it away.
    const costMs = gpuValid ? (gpuMs > cpuMs ? gpuMs : cpuMs) : cpuMs;

    this.frameRing[this.ringHead] = frameMs;
    this.costRing[this.ringHead] = costMs;
    this.ringHead = this.ringHead + 1 === WINDOW ? 0 : this.ringHead + 1;
    if (this.ringLen < WINDOW) this.ringLen++;
    this.samples++;
    this.frames++;
    this.ema = this.samples === 1 ? costMs : this.ema + (costMs - this.ema) * EMA_ALPHA;

    if (!this.auto || this.batched) return;

    const budget = this.budgetMs;
    // Two independent tells. Cost catches a GPU saturated behind vsync, where the
    // wall delta pegs at 16.7 and says nothing; the wall delta catches everything
    // the timer cannot see, the sim included.
    const dropped = frameMs > this.targetMs * DROP_FACTOR;
    const dt = (frameMs > 50 ? 50 : frameMs) / 1000;
    if (costMs > budget || dropped) {
      let severity = costMs / budget;
      if (severity < 1) severity = 1;
      else if (severity > 4) severity = 4;
      this.overrun += dt * severity;
    } else {
      this.overrun -= dt * BLEED;
      if (this.overrun < 0) this.overrun = 0;
    }

    // §14.6 consecutive-frame counters over the GPU EMA.
    if (this.ema > cfg.get('perf.gpu_up_threshold_ms')) this.upFrames++;
    else this.upFrames = 0;
    if (this.ema < cfg.get('perf.gpu_down_threshold_ms')) this.downFrames++;
    else this.downFrames = 0;

    if (this.samples < WARMUP) return;

    const since = this.frames - this.changedFrame;

    // Fast path: a genuine cliff, walked at half a second per rung.
    if (
      this.overrun >= DEGRADE_SECONDS &&
      since >= EMERGENCY_MIN_FRAMES &&
      nowMs - this.changedMs >= EMERGENCY_SETTLE_MS
    ) {
      this.setRung(this.rungId + 1, 1);
      return;
    }

    if (since < cfg.get('perf.step_cooldown_frames')) return;

    // §14.6 steady path: EMA over 7.0 ms for 30 consecutive frames.
    if (this.upFrames >= cfg.get('perf.up_frames') && this.rungId < LADDER.length) {
      this.setRung(this.rungId + 1, 1);
      return;
    }

    this.evalCount++;
    if (this.evalCount < EVAL_EVERY) return;
    this.evalCount = 0;
    if (this.ringLen < WINDOW) return;
    if (this.maybeDegrade()) return;
    this.maybeRestore();
  }

  /** A copy of the current state, for the run summary and the bench artifact. */
  snapshot(): PerfSnapshot {
    return {
      tier: this.tierId,
      rung: this.rungId,
      rungs: LADDER.length,
      label: this.label,
      autoBalance: this.auto,
      generation: this.gen,
      targetMs: this.targetMs,
      budgetMs: this.budgetMs,
      gpuEmaMs: this.ema,
      frameMsP50: this.percentile(this.frameRing, 0.5),
      frameMsP95: this.percentile(this.frameRing, 0.95),
      costMsP50: this.percentile(this.costRing, 0.5),
      costMsP95: this.percentile(this.costRing, 0.95),
      samples: this.samples,
      gpuTimed: this.gpuTimed,
      overrunS: this.overrun,
      restoreDwellFrames: this.restoreDwellFrames,
      caps: { ...this.caps },
      verdict: this.verdictHeld,
      timeline: this.log.slice(),
    };
  }

  /** Every rung's label, in order. The dev overlay and the tests read this. */
  ladderLabels(): string[] {
    const out: string[] = [];
    for (let i = 0; i < LADDER.length; i++) out.push(LADDER[i].label);
    return out;
  }

  /** Caps as they would stand at `rung` on `tier`, without touching live state. */
  capsAt(tier: DeviceTier, rung: number): PerfCaps {
    const c: PerfCaps = { ...TIER_CAPS[tier] };
    const n = rung > LADDER.length ? LADDER.length : rung < 0 ? 0 : rung;
    for (let i = 0; i < n; i++) LADDER[i].apply(c);
    return c;
  }

  /**
   * The boot probe: render honest frames through the real chain, once per
   * candidate tier, and pick the opening tier and rung.
   *
   * `renderOneFrame` must draw exactly what the loop draws, and should end with a
   * GPU sync (a 1×1 `readPixels`, or `gl.finish()`) — without one the number is
   * submission cost and a GPU-bound machine looks free. The probe compensates as
   * far as it can by watching for dropped animation frames during the walk, but
   * a sync is the honest measurement.
   *
   * Never fatal: anything unexpected returns the current tier and leaves the
   * governor to work it out from rung 0.
   */
  async probe(renderOneFrame: () => void): Promise<DeviceTier> {
    const allowanceMs = this.budgetMs * QUIET_FRACTION;
    try {
      const stored = this.readStored();
      if (stored !== null) {
        this.verdictHeld = stored;
        this.setTier(stored.tier, false);
        this.armFrom(stored.rung);
        return stored.tier;
      }

      const quiet: Partial<Record<DeviceTier, number>> = {};
      const start = now();
      let timed = 0;
      let compileMs = 0;

      for (let i = 0; i < TIER_ORDER.length; i++) {
        const tier = TIER_ORDER[i];
        const cheapest = i === TIER_ORDER.length - 1;
        const added = now() - start - compileMs;
        if ((timed >= PROBE_TIMED_BUDGET_MS || added >= PROBE_WALL_BUDGET_MS) && !cheapest) continue;
        if (added >= PROBE_WALL_BUDGET_MS * 2) break;

        this.tierId = tier;
        this.rungId = 0;
        this.applyRung();
        this.gen++;

        // Warmup compiles the composite, the bloom pyramid and every material.
        // The first real frame pays that bill whether the probe exists or not, so
        // it is measured and then excluded from the leash rather than hidden in it.
        const warm0 = now();
        renderOneFrame();
        const warm = now() - warm0;
        if (i === 0) compileMs = warm;
        await nextFrame();

        const t0 = now();
        const ms = await this.bestFrame(renderOneFrame, allowanceMs * 2);
        timed += now() - t0;
        quiet[tier] = ms;
        if (ms <= allowanceMs * TIER_SLACK) break;
      }

      const v = this.verdictFor(quiet, allowanceMs, true);
      this.verdictHeld = v;
      this.saveSample(quiet);
      this.setTier(v.tier, false);
      this.armFrom(v.rung);
      return v.tier;
    } catch {
      // A machine the probe cannot measure gets the current tier, rung 0, and the
      // governor working it out from there. Nothing on screen says so.
      this.verdictHeld = {
        tier: this.tierId,
        rung: 0,
        quietMs: 0,
        allowanceMs,
        reason: 'probe failed; governor starts at rung 0',
        fresh: false,
      };
      return this.tierId;
    }
  }

  /** Back to rung 0 with a clean window, keeping the tier. */
  reset(): void {
    this.resetWindow();
    if (this.rungId !== 0) {
      this.rungId = 0;
      this.applyRung();
      this.gen++;
      this.writeConfigCaps();
    }
    this.log.length = 0;
    this.restoreDwellFrames = cfg.get('perf.down_frames');
  }

  /** Drops the stored verdict for this machine so the next probe measures again. */
  forgetStored(): void {
    const s = storage();
    if (s === null) return;
    try {
      s.removeItem(`${STORE_PREFIX}.${this.signature()}`);
    } catch {
      // A store that will not delete is not worth failing a boot over.
    }
  }

  // ─────────────────────────────────────────────────────────────── internals ──

  /**
   * The tail path.
   *
   * The overrun accumulator answers "is this bad *now*" and is deliberately hard
   * to move: one hitch in twenty bleeds off before it buys a rung. But one frame
   * in twenty over budget is exactly the judder the player complains about, and
   * no accumulator with a 2× bleed will ever see it. So the p95 gets a rule of
   * its own, over a window entirely post-change, and it is the same statistic the
   * restore rule reads — which is what makes the hysteresis band a band rather
   * than two unrelated thresholds.
   */
  private maybeDegrade(): boolean {
    if (this.rungId >= LADDER.length) return false;
    if (this.percentile(this.costRing, 0.95) <= this.budgetMs) return false;
    this.setRung(this.rungId + 1, 1);
    return true;
  }

  private maybeRestore(): void {
    if (this.rungId === 0) return;
    if (this.frames - this.changedFrame < this.restoreDwellFrames) return;
    if (this.downFrames < cfg.get('perf.down_frames')) return;
    // Any pressure at all in the recent past disqualifies a restore outright.
    if (this.overrun > 0) return;
    if (this.percentile(this.costRing, 0.95) >= this.budgetMs * RESTORE_FRACTION) return;
    if (this.percentile(this.frameRing, 0.95) > this.targetMs * 1.2) return;
    this.setRung(this.rungId - 1, -1);
  }

  /**
   * Hands every rung back without touching the dwell bookkeeping.
   *
   * It still writes to the log: without the entry a trace reads as two descents
   * to rung 1 with no ascent between them, which looks like a governor that lost
   * count rather than one that stood aside for a screenshot.
   */
  private forceFull(): void {
    if (this.rungId === 0) return;
    this.rungId = 0;
    this.overrun = 0;
    this.applyRung();
    this.gen++;
    this.writeConfigCaps();
    this.pushLog(0, 'capture · full quality', -1);
  }

  private applyRung(): void {
    const c = this.caps;
    const base = TIER_CAPS[this.tierId];
    c.maxEnemies = base.maxEnemies;
    c.maxProjectiles = base.maxProjectiles;
    c.maxParticles = base.maxParticles;
    c.bufferScale = base.bufferScale;
    c.pixelRatioCap = base.pixelRatioCap;
    c.persistence = base.persistence;
    c.halation = base.halation;
    c.bloomLevels = base.bloomLevels;
    c.particleScale = base.particleScale;
    c.trailScale = base.trailScale;
    c.parallaxLayers = base.parallaxLayers;
    c.sparkScale = base.sparkScale;
    c.trailWidthScale = base.trailWidthScale;
    for (let i = 0; i < this.rungId; i++) LADDER[i].apply(c);
  }

  /**
   * Mirror the live entity caps into the config registry.
   *
   * AMD-2 makes the caps part of the run's config snapshot, so `content_hash`
   * carries the tier and a replay only reproduces on the same tier — already true
   * of any tuning change (A.5). It also means the director's §8.4 spawn pre-check
   * sees the tier cap through the path it already reads, with no new coupling.
   * A frozen registry (release boot) simply keeps whatever it was frozen with.
   */
  private writeConfigCaps(): void {
    if (cfg.isFrozen) return;
    try {
      cfg.set('budget.max_enemies', this.caps.maxEnemies);
      cfg.set('budget.max_projectiles', this.caps.maxProjectiles);
      cfg.set('budget.max_particles', this.caps.maxParticles);
    } catch {
      // Registry frozen between the check and the write. Nothing to do.
    }
  }

  private resetWindow(): void {
    this.ringHead = 0;
    this.ringLen = 0;
    this.samples = 0;
    this.evalCount = 0;
    this.ema = 0;
    this.upFrames = 0;
    this.downFrames = 0;
    this.lastSampleMs = -1;
    this.overrun = 0;
    this.changedFrame = -1e9;
    this.changedMs = -1e9;
    this.restoredFrame = -1e9;
  }

  private pushLog(rung: number, label: string, dir: 1 | -1): void {
    if (this.log.length >= LOG_MAX) this.log.shift();
    this.log.push({ t: now(), frame: this.frames, rung, label, dir });
  }

  /**
   * True when a real presentation happened since the last call.
   *
   * `document.timeline.currentTime` is frozen for the whole of one rendering
   * update, so a synchronous loop of renders — exactly what the capture harness
   * does — reads the same value every time.
   */
  private timelineAdvanced(): boolean {
    if (typeof document === 'undefined') return true;
    const t = document.timeline?.currentTime;
    if (typeof t !== 'number') return true;
    if (t === this.lastTimeline) return false;
    this.lastTimeline = t;
    return true;
  }

  /** Insertion sort over a preallocated scratch. No allocation, no `Array.sort`. */
  private percentile(ring: Float64Array, q: number): number {
    const n = this.ringLen;
    if (n === 0) return 0;
    const s = this.scratch;
    for (let i = 0; i < n; i++) s[i] = ring[i];
    for (let i = 1; i < n; i++) {
      const v = s[i];
      let j = i - 1;
      while (j >= 0 && s[j] > v) {
        s[j + 1] = s[j];
        j--;
      }
      s[j + 1] = v;
    }
    let k = Math.round(q * (n - 1));
    if (k < 0) k = 0;
    else if (k >= n) k = n - 1;
    return s[k];
  }

  /**
   * Cost of the cheapest of {@link PROBE_FRAMES} frames, in ms.
   *
   * The *minimum*, not the median, and that is the difference between a verdict
   * and a coin toss. Boot is the noisiest moment in the session: fonts are
   * landing, the HUD is laying out, the GPU is still in a low power state, and
   * every one of those makes the machine look slower than it is. The minimum
   * answers what this machine can do when nothing is in its way and leaves what
   * it *sustains* to the governor, which has thousands of frames to work with.
   *
   * A frame the browser then failed to present inside the target is counted, and
   * a majority of dropped frames raises the verdict floor — the one correction
   * available without a GPU timer query this early.
   */
  private async bestFrame(renderOneFrame: () => void, ceilingMs: number): Promise<number> {
    let best = Infinity;
    let dropped = 0;
    let taken = 0;
    let last = now();
    for (let i = 0; i < PROBE_FRAMES; i++) {
      const t0 = now();
      renderOneFrame();
      const ms = now() - t0;
      if (ms < best) best = ms;
      taken++;
      await nextFrame();
      const delta = now() - last;
      last = now();
      if (i > 0 && delta > this.targetMs * DROP_FACTOR) dropped++;
      // Two frames, both at twice what the tier is allowed, have answered the
      // question, and on those machines each further look is expensive. Two
      // rather than one because a single slow frame is the boot noise this whole
      // function exists to see past.
      if (best > ceilingMs && i >= 1) break;
    }
    if (!Number.isFinite(best)) return 0;
    if (taken > 2 && dropped * 2 >= taken) {
      const floor = this.targetMs * 0.9;
      if (best < floor) best = floor;
    }
    return best;
  }

  private verdictFor(
    quiet: Partial<Record<DeviceTier, number>>,
    allowanceMs: number,
    fresh: boolean,
  ): ProbeVerdict {
    for (let i = 0; i < TIER_ORDER.length; i++) {
      const tier = TIER_ORDER[i];
      const ms = quiet[tier];
      if (ms === undefined || ms > allowanceMs * TIER_SLACK) continue;
      return {
        tier,
        rung: rungFor(ms, allowanceMs),
        quietMs: ms,
        allowanceMs,
        reason: `${ms.toFixed(1)} ms quiet frame against ${allowanceMs.toFixed(1)} ms allowed`,
        fresh,
      };
    }
    // Nothing fitted. The cheapest tier is the answer whether or not the budget
    // stretched far enough to measure it: an unmeasured `handheld` is a guess in
    // the one direction the governor undoes for free.
    let best = 0;
    for (let i = 0; i < TIER_ORDER.length; i++) {
      const ms = quiet[TIER_ORDER[i]];
      if (ms !== undefined && (best === 0 || ms < best)) best = ms;
    }
    const floor = TIER_ORDER[TIER_ORDER.length - 1];
    if (best > 0) {
      const r = rungFor(best, allowanceMs);
      return {
        tier: floor,
        rung: r < 1 ? 1 : r,
        quietMs: best,
        allowanceMs,
        reason: `${best.toFixed(1)} ms quiet frame over ${allowanceMs.toFixed(1)} ms at every tier`,
        fresh,
      };
    }
    return { tier: 'desktop', rung: 0, quietMs: 0, allowanceMs, reason: 'not measured', fresh };
  }

  // ────────────────────────────────────────────────────────────── persistence ──

  /**
   * What makes two boots the same machine.
   *
   * The GPU string is the whole point. `devicePixelRatio` rides along because it
   * decides how much the tier's DPR cap is actually capping, and the viewport
   * because the composite runs at output resolution.
   */
  private signature(): string {
    const dpr = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
    const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
    const cores = typeof navigator === 'undefined' ? 0 : navigator.hardwareConcurrency || 0;
    const w = typeof screen === 'undefined' ? 0 : screen.width || 0;
    const h = typeof screen === 'undefined' ? 0 : screen.height || 0;
    return fnv36(`${this.gpuSig}|${ua}|${cores}|${dpr}|${w}x${h}`);
  }

  private readStored(): ProbeVerdict | null {
    const s = storage();
    if (s === null) return null;
    try {
      const raw = s.getItem(`${STORE_PREFIX}.${this.signature()}`);
      if (raw === null) return null;
      const blob = JSON.parse(raw) as {
        v?: number;
        quiet?: Partial<Record<DeviceTier, number>>;
        chosen?: DeviceTier;
      };
      if (blob === null || blob.v !== 1 || typeof blob.quiet !== 'object' || blob.quiet === null) {
        return null;
      }
      const allowanceMs = this.budgetMs * QUIET_FRACTION;
      const v = this.verdictFor(blob.quiet, allowanceMs, false);
      if (blob.chosen !== undefined && TIER_CAPS[blob.chosen] !== undefined && blob.chosen !== v.tier) {
        // The player's tier, with the probe's rung carried across only for the
        // floor tier: a chosen tier is a ceiling, and opening several rungs under
        // a ceiling set by hand is the governor's business to earn.
        return {
          tier: blob.chosen,
          rung: blob.chosen === 'handheld' ? v.rung : 0,
          quietMs: v.quietMs,
          allowanceMs,
          reason: 'tier chosen in settings',
          fresh: false,
        };
      }
      return v;
    } catch {
      return null;
    }
  }

  private saveSample(quiet: Partial<Record<DeviceTier, number>>): void {
    const s = storage();
    if (s === null) return;
    const key = `${STORE_PREFIX}.${this.signature()}`;
    try {
      // One machine keeps one row. An old signature is a GPU or a display this
      // browser is no longer on, and leaving it behind leaks blobs nothing reads.
      for (let i = s.length - 1; i >= 0; i--) {
        const k = s.key(i);
        if (k !== null && k !== key && k.startsWith(STORE_PREFIX)) s.removeItem(k);
      }
      const prev = s.getItem(key);
      let chosen: DeviceTier | undefined;
      if (prev !== null) {
        const old = JSON.parse(prev) as { chosen?: DeviceTier };
        chosen = old?.chosen;
      }
      s.setItem(key, JSON.stringify({ v: 1, quiet, chosen }));
    } catch {
      // A full or disabled store is not a reason to fail a boot.
    }
  }

  /**
   * Records that the player picked a tier by hand.
   *
   * Without this a player who drops to `handheld` on a machine the probe rates
   * `desktop` is silently overruled on the next load, and the probe becomes
   * something that argues with them once per boot.
   */
  private rememberChoice(tier: DeviceTier): void {
    const s = storage();
    if (s === null) return;
    const key = `${STORE_PREFIX}.${this.signature()}`;
    try {
      const prev = s.getItem(key);
      const blob = prev === null ? { v: 1, quiet: {} } : (JSON.parse(prev) as Record<string, unknown>);
      blob.v = 1;
      blob.chosen = tier;
      s.setItem(key, JSON.stringify(blob));
    } catch {
      // As above.
    }
  }
}

function rungFor(quietMs: number, allowanceMs: number): number {
  if (!(quietMs > 0) || !(allowanceMs > 0)) return 0;
  const load = quietMs / allowanceMs;
  for (let i = 0; i < RUNG_STEPS.length; i++) if (load < RUNG_STEPS[i].under) return RUNG_STEPS[i].rung;
  return RUNG_STEPS[RUNG_STEPS.length - 1].rung;
}

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Safari in a blocked third-party frame throws on the getter itself.
    return null;
  }
}

/** FNV-1a, base36. Short enough to read in devtools, wide enough not to collide. */
function fnv36(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36);
}

/** One animation frame, or a macrotask where there is no document. */
function nextFrame(): Promise<void> {
  return new Promise<void>((resolve) => {
    if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => resolve());
    else setTimeout(resolve, 0);
  });
}

/** The one governor. Everything that measures or obeys it talks to this. */
export const perf = new PerfGovernor();
